import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useEntity } from 'entities/hooks';
import { selectStoryPagesWithMedia } from 'story-pages/collection';
import { getStoryPages, movePagesToStory } from 'story-pages/actions';
import { createStory } from 'stories/actions';
import ConfirmSplitStory from './confirm';

// "Split Story from here" (see story-media/edit-buttons.js): confirming
// creates the new story right away (no separate Story Settings step — the
// member gives it a title/cover through the normal new-story flow it lands
// on), moves the selected page and everything after it (within the current
// story) into it, then navigates there. No page/story is duplicated or
// re-uploaded — see the comment on movePagesToStory for how the move itself
// works.
const SplitStory = ({ storyId, pageId, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const { push } = useHistory();
  const { pathname } = useLocation();

  const { entity: story } = useEntity({ id: storyId, type: 'stories' });
  const journalId = story?.['journal-id'] || story?.['journal-ids']?.[0];

  const pages = useSelector(state => selectStoryPagesWithMedia({ state, storyId })) || [];
  const splitIndex = pages.findIndex(page => page.id === pageId);
  const pagesToMove = splitIndex === -1 ? [] : pages.slice(splitIndex);

  const handleSplitStory = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await dispatch(createStory({ 'journal-id': journalId }));
      const [newStoryId] = result?.payload?.data?.result?.stories || [];
      if (!newStoryId) throw new Error('The new story could not be created. Please try again.');

      if (pagesToMove.length) {
        await dispatch(movePagesToStory(pagesToMove, newStoryId));
        await dispatch(getStoryPages(storyId));
      }

      // Lands on the normal "new story" flow — same place any other new story
      // starts — so title, cover, etc. are picked up from there as usual.
      const base = pathname.includes('/tell_a_story') ? '/tell_a_story/stories/new' : '/stories/new';
      push(`${base}/${newStoryId}`);
    } catch (err) {
      setError(err);
      setIsSubmitting(false);
    }
  };

  return (
    <ConfirmSplitStory
      onConfirm={handleSplitStory}
      onClose={onClose}
      isSubmitting={isSubmitting}
      error={error}
    />
  );
};

SplitStory.propTypes = {
  storyId: PropTypes.string.isRequired,
  pageId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SplitStory;
